import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type {
  CaseListView,
  CaseView,
  CreateCaseRequest,
  DeclareScopeRequest,
  PatchApplicabilityRequest,
  ProgramApplicabilityView,
  SubmissionScopeMutationResult,
} from '../types/case'
import type { ProgramFilingAdjustmentView, ProgramFilingDetailView } from '../types/programFilingDetail'

/**
 * Typed fetch wrappers for `module.case_`'s `Case`/`ProgramFiling`/`SubmissionScope`/
 * `ProgramApplicability` surface — same thin-wrapper convention as `api/accounts.ts`.
 *
 * IB-326 — `createCase`/`declareSubmissionScope` are the "New Case" wizard's ONLY two writes: the
 * Case shell, then the submission-scope election that materializes its first ProgramFiling(s)
 * (FR-M6.2/M6.3). Neither touches a guarded field — `qre_pct`/`qualifying_pct`/in-state/credit
 * figures stay on the `AdjustmentService` path exclusively (CLAUDE.md §2.1); this file adds no
 * such write.
 */

export interface ListCasesParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  sort?: string | null
  cursor?: string | null
  /** Clamped to 200 server-side; default page size here is 50. */
  limit?: number
}

function toQuery(params: ListCasesParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

/** Backs `screens/cases/CasesListScreen.tsx` — the denormalized `CaseListView` row (names, never UUIDs). */
export async function listCases(
  params: ListCasesParams,
  signal?: AbortSignal,
): Promise<CursorPage<CaseListView>> {
  const { data } = await apiClient.get<CursorPage<CaseListView>>(`/cases?${toQuery(params)}`, { signal })
  return data
}

/** Backs `screens/cases/CaseDetailScreen.tsx`'s header — the bare `CaseView` (single-record reads don't need denormalization). */
export async function getCase(id: string, signal?: AbortSignal): Promise<CaseView> {
  const { data } = await apiClient.get<CaseView>(`/cases/${id}`, { signal })
  return data
}

/**
 * IB-326 AC1 — `POST /api/v1/cases` (`CaseController.create`). The wizard's first write: a bare
 * Case shell (Account/Legal entity/kind + its one companion pointer). Not idempotency-key-covered
 * server-side (`IdempotencyFilterConfig` deliberately excludes `/api/v1/cases` — see its own
 * comment) — this wrapper sends none, matching every other existing caller of this route.
 */
export async function createCase(request: CreateCaseRequest): Promise<CaseView> {
  const { data } = await apiClient.post<CaseView>('/cases', request)
  return data
}

/**
 * IB-326 AC2 — `POST /api/v1/cases/{caseId}/submission-scope` (`SubmissionScopeController.declare`).
 * The wizard's second and final write: the one-time SubmissionScope election that materializes the
 * Case's first `ProgramFiling`(s) in the SAME backend transaction (`MaterializationService.reconcile`)
 * — the response's `created`/`revived` arrays are exactly which filings resulted (AC5).
 */
export async function declareSubmissionScope(
  caseId: string,
  request: DeclareScopeRequest,
): Promise<SubmissionScopeMutationResult> {
  const { data } = await apiClient.post<SubmissionScopeMutationResult>(
    `/cases/${caseId}/submission-scope`,
    request,
  )
  return data
}

/**
 * IB-326 — `POST /api/v1/legal-entities/{legalEntityId}/program-applicability/determine` (footprint
 * auto-determination, FR-M6.2). Feeds the wizard's "specific" state-mode program picker: only
 * `applicable=true` rows are offered as selectable `selectedProgramIds`.
 */
export async function determineProgramApplicability(
  legalEntityId: string,
  kind: string,
  fiscalYearId?: string | null,
): Promise<ProgramApplicabilityView[]> {
  const q = new URLSearchParams({ kind })
  if (fiscalYearId) q.set('fiscalYearId', fiscalYearId)
  const { data } = await apiClient.post<ProgramApplicabilityView[]>(
    `/legal-entities/${legalEntityId}/program-applicability/determine?${q.toString()}`,
    undefined,
  )
  return data
}

/**
 * IB-326 AC2 — the manual-override affordance (`PATCH /api/v1/program-applicability/{id}`,
 * `ProgramApplicabilityController.override`). `basis` becomes `'manual'` server-side
 * unconditionally; this wrapper never sends a `basis` field itself.
 */
export async function overrideProgramApplicability(
  id: string,
  request: PatchApplicabilityRequest,
): Promise<ProgramApplicabilityView> {
  const { data } = await apiClient.patch<ProgramApplicabilityView>(`/program-applicability/${id}`, request)
  return data
}

export interface ListCaseFilingsParams {
  cursor?: string | null
  limit?: number
}

/** `GET /cases/{caseId}/program-filings` — the Case detail's related-filings list. */
export async function listCaseProgramFilings(
  caseId: string,
  params: ListCaseFilingsParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<ProgramFilingDetailView>> {
  const q = new URLSearchParams()
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  const { data } = await apiClient.get<CursorPage<ProgramFilingDetailView>>(
    `/cases/${caseId}/program-filings?${q.toString()}`,
    { signal },
  )
  return data
}

/**
 * `GET /api/v1/program-filings/{id}/adjustments` (`ProgramFilingAdjustmentHistoryController`,
 * docs/adr-log.md ADR-0041, IB-282 View 2 AC2.5/RAID R-UX282-3 — a small, additive read this story
 * adds, mirroring `listSpendLineAdjustments` exactly) — a **read-only** GET wrapper. Newest-first,
 * matching the backend's own ordering; this wrapper does not re-sort.
 */
export async function listProgramFilingAdjustments(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<ProgramFilingAdjustmentView[]> {
  const { data } = await apiClient.get<ProgramFilingAdjustmentView[]>(
    `/program-filings/${programFilingId}/adjustments`,
    { signal },
  )
  return data
}
